import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CostTrends as CostTrendsData, MetricPoint } from '@lizrd/contracts';
import { getCostTrends } from '../lib/api';
import { LineChart, type LineSeries } from './ui/line-chart';
import { SkeletonChart } from './ui/skeleton';
import { categoryColor, categoryOf, PROVIDER_ACCENT, usd } from '../lib/graph-visuals';

const RANGES = [
  { days: 7, label: '7D' },
  { days: 30, label: '30D' },
  { days: 90, label: '90D' },
] as const;

const PROVIDER_LABEL: Record<string, string> = {
  AWS: 'AWS',
  AZURE: 'Azure',
  GCP: 'GCP',
};

/** Sum several day-aligned point arrays into one (all share the same axis). */
function sumSeries(arrays: MetricPoint[][]): MetricPoint[] {
  const base = arrays[0];
  if (!base) return [];
  return base.map((p, i) => ({
    day: p.day,
    value: Math.round(arrays.reduce((s, a) => s + (a[i]?.value ?? 0), 0) * 100) / 100,
  }));
}

/**
 * The Explorer "Trend" tier (third view on the cost box): cost over time as (1)
 * the domain total, (2) per cloud provider — multiple accounts of a
 * provider accumulate into one badged line, and (3) per resource type across
 * clouds. A range filter (7/30/90d) and per-series legend toggles. Built on the
 * temporal observation log (CT-S3).
 */
export function CostTrends({ envId }: { envId: string }) {
  const [days, setDays] = useState(30);
  const trends = useQuery({
    queryKey: ['cost-trends', envId, days],
    queryFn: () => getCostTrends(envId, days),
    enabled: !!envId,
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted">
          Estimated cost over time — the trend fills in one point per day as syncs run.
        </p>
        <div className="inline-flex overflow-hidden rounded-sm border border-border text-[0.7rem]">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`px-2.5 py-1 ${days === r.days ? 'bg-iris text-on-iris' : 'bg-surface text-muted hover:text-bone'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {trends.isLoading ? (
        <SkeletonChart height="h-40" />
      ) : (trends.data?.total.length ?? 0) < 2 ? (
        <div className="grid h-40 place-items-center rounded-md border border-hairline bg-surface-2/40 px-6 text-center text-sm text-muted">
          Not enough history yet — the trend appears once at least two daily sync snapshots have
          accumulated. Check back after your next sync.
        </div>
      ) : (
        <TrendCharts data={trends.data!} />
      )}
    </div>
  );
}

function TrendCharts({ data }: { data: CostTrendsData }) {
  // Per-provider series (accounts already combined server-side).
  const providerSeries: LineSeries[] = data.providers.map((p) => ({
    key: p.provider,
    label:
      (PROVIDER_LABEL[p.provider] ?? p.provider) +
      (p.accountCount > 1 ? ` · ${p.accountCount} accounts` : ''),
    color: PROVIDER_ACCENT[p.provider] ?? '#8b93a7',
    points: p.points,
  }));

  // Resource types rolled up into categories, across all clouds.
  const categorySeries: LineSeries[] = useMemo(() => {
    const byCategory = new Map<string, MetricPoint[][]>();
    for (const t of data.types) {
      const cat = categoryOf(t.type);
      (byCategory.get(cat) ?? byCategory.set(cat, []).get(cat)!).push(t.points);
    }
    return [...byCategory.entries()]
      .map(([cat, arrays]) => ({
        key: cat,
        label: cat,
        color: categoryColor(cat),
        points: sumSeries(arrays),
      }))
      .sort((a, b) => (b.points.at(-1)?.value ?? 0) - (a.points.at(-1)?.value ?? 0));
  }, [data.types]);

  const totalSeries: LineSeries[] = [
    { key: 'total', label: 'Total', color: 'var(--iris)', points: data.total },
  ];
  const latestTotal = data.total.at(-1)?.value ?? 0;

  return (
    <div className="space-y-5">
      <ChartCard
        title="Total Cost Trend"
        subtitle={`Currently ${usd(latestTotal)}/mo across all connected clouds`}
        series={totalSeries}
      />
      <ChartCard
        title="Cost by Cloud Provider"
        subtitle={
          data.providers.some((p) => p.accountCount > 1)
            ? 'Multiple accounts of a provider are combined into one line'
            : 'One line per connected cloud provider'
        }
        series={providerSeries}
        toggleable
      />
      <ChartCard
        title="Cost by Resource Type"
        subtitle="Across all clouds, grouped into categories"
        series={categorySeries}
        toggleable
      />
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  series,
  toggleable = false,
}: {
  title: string;
  subtitle: string;
  series: LineSeries[];
  toggleable?: boolean;
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const shown = series.filter((s) => !hidden.has(s.key));

  return (
    <div className="rounded-md border border-hairline bg-surface-2/30 p-4">
      <div className="mb-1">
        <h3 className="font-display text-sm font-bold text-bone">{title}</h3>
        <p className="text-[0.7rem] text-muted">{subtitle}</p>
      </div>

      {toggleable && series.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {series.map((s) => {
            const off = hidden.has(s.key);
            return (
              <button
                key={s.key}
                onClick={() =>
                  setHidden((h) => {
                    const next = new Set(h);
                    if (next.has(s.key)) next.delete(s.key);
                    else next.add(s.key);
                    return next;
                  })
                }
                className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.65rem] transition-colors ${
                  off ? 'border-border text-muted/50' : 'border-border bg-surface text-bone'
                }`}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: off ? 'var(--muted)' : s.color }}
                />
                {s.label}
              </button>
            );
          })}
        </div>
      )}

      <LineChart series={shown} />
    </div>
  );
}
